import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

// 报销状态枚举
enum ReimbursementStatus {
    PENDING = "pending", // 待审核
    APPROVED = "approved", // 已批准
    REJECTED = "rejected", // 已拒绝
    PAID = "paid" // 已支付
}

// 报销类型枚举
enum ReimbursementType {
    TEACHING_MATERIALS = "teaching_materials", // 教学材料
    TRAVEL = "travel", // 差旅费
    OFFICE_SUPPLIES = "office_supplies", // 办公用品
    EQUIPMENT = "equipment", // 设备
    OTHER = "other" // 其他
}

// 文件类型枚举
enum FileType {
    PDF = "pdf",
    WORD = "word",
    EXCEL = "excel",
    IMAGE = "image",
    OTHER = "other"
}

// 文件信息接口
interface FileInfo {
    url: string;
    name: string;
    type: FileType;
    uploadedAt: Date;
}

// 报销记录接口
interface Reimbursement {
    id: string;
    title: string;
    description: string;
    amount: number;
    type: ReimbursementType;
    receipts: FileInfo[] | string[];  // 兼容旧格式
    additionalDocuments?: FileInfo[] | string[];  // 兼容旧格式
    status: ReimbursementStatus;
    reviewComment?: string;
    reviewedBy?: any;
    reviewedAt?: Date;
    paymentMethod?: string;
    bankAccount?: {
        accountName: string;
        accountNumber: string;
        bankName: string;
    };
    paymentDate?: Date;
    createdBy: any;
    createdAt: Date;
    updatedAt: Date;
}

// 从URL中获取文件类型
const getFileType = (url: string): FileType => {
    try {
        // 从URL中提取文件名（去除查询参数）
        const urlWithoutParams = url.split('?')[0];
        const fileName = urlWithoutParams.split('/').pop() || '';

        // 检查文件扩展名
        if (fileName.toLowerCase().includes('.pdf')) {
            return FileType.PDF;
        } else if (fileName.toLowerCase().includes('.doc') || fileName.toLowerCase().includes('.docx')) {
            return FileType.WORD;
        } else if (fileName.toLowerCase().includes('.xls') || fileName.toLowerCase().includes('.xlsx')) {
            return FileType.EXCEL;
        } else if (
            fileName.toLowerCase().includes('.jpg') ||
            fileName.toLowerCase().includes('.jpeg') ||
            fileName.toLowerCase().includes('.png') ||
            fileName.toLowerCase().includes('.gif') ||
            fileName.toLowerCase().includes('.webp')
        ) {
            return FileType.IMAGE;
        }

        // 如果URL中包含文件类型信息
        if (url.toLowerCase().includes('content-type=application/pdf')) {
            return FileType.PDF;
        } else if (url.toLowerCase().includes('content-type=application/msword') ||
            url.toLowerCase().includes('content-type=application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            return FileType.WORD;
        } else if (url.toLowerCase().includes('content-type=application/vnd.ms-excel') ||
            url.toLowerCase().includes('content-type=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
            return FileType.EXCEL;
        } else if (url.toLowerCase().includes('content-type=image/')) {
            return FileType.IMAGE;
        }

        // 默认返回其他类型
        return FileType.OTHER;
    } catch (error) {
        console.error('Error determining file type:', error);
        return FileType.OTHER;
    }
};

// 从URL中提取文件名
const getFileNameFromUrl = (url: string): string => {
    try {
        // 从URL中提取文件名（去除查询参数）
        const urlWithoutParams = url.split('?')[0];
        const fileName = urlWithoutParams.split('/').pop() || '未知文件';
        // 解码URL编码的文件名
        return decodeURIComponent(fileName);
    } catch (error) {
        console.error('Error extracting filename from URL:', error);
        return '未知文件';
    }
};

const ReimbursementDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [reimbursement, setReimbursement] = useState<Reimbursement | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const currentLocale = i18n.language === 'zh' ? zhCN : enUS;
    const [openLightbox, setOpenLightbox] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [processedReceipts, setProcessedReceipts] = useState<FileInfo[]>([]);
    const [processedDocuments, setProcessedDocuments] = useState<FileInfo[]>([]);

    useEffect(() => {
        const fetchReimbursement = async () => {
            if (!id || !auth.currentUser) {
                setError(t('reimbursements.notFound'));
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, 'reimbursements', id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    
                    // 处理日期字段
                    const reimbursementData = {
                        id: docSnap.id,
                        ...data,
                        createdAt: data.createdAt?.toDate(),
                        updatedAt: data.updatedAt?.toDate(),
                        reviewedAt: data.reviewedAt?.toDate(),
                        paymentDate: data.paymentDate?.toDate(),
                    } as Reimbursement;
                    
                    setReimbursement(reimbursementData);
                    
                    // 处理收据数据
                    if (data.receipts) {
                        // 如果是旧格式（字符串数组），则转换为新格式
                        if (typeof data.receipts[0] === 'string') {
                            const convertedReceipts = data.receipts.map((url: string) => ({
                                url,
                                name: getFileNameFromUrl(url),
                                type: getFileType(url),
                                uploadedAt: data.updatedAt?.toDate() || new Date()
                            }));
                            setProcessedReceipts(convertedReceipts);
                        } else {
                            // 已经是新格式
                            setProcessedReceipts(data.receipts.map((receipt: any) => ({
                                ...receipt,
                                uploadedAt: receipt.uploadedAt?.toDate() || new Date()
                            })));
                        }
                    }
                    
                    // 处理附加文档数据
                    if (data.additionalDocuments) {
                        // 如果是旧格式（字符串数组），则转换为新格式
                        if (typeof data.additionalDocuments[0] === 'string') {
                            const convertedDocs = data.additionalDocuments.map((url: string) => ({
                                url,
                                name: getFileNameFromUrl(url),
                                type: getFileType(url),
                                uploadedAt: data.updatedAt?.toDate() || new Date()
                            }));
                            setProcessedDocuments(convertedDocs);
                        } else {
                            // 已经是新格式
                            setProcessedDocuments(data.additionalDocuments.map((doc: any) => ({
                                ...doc,
                                uploadedAt: doc.uploadedAt?.toDate() || new Date()
                            })));
                        }
                    }
                } else {
                    setError(t('reimbursements.notFound'));
                }
            } catch (err) {
                console.error('获取报销记录失败:', err);
                setError(t('reimbursements.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchReimbursement();
    }, [id, t]);

    const showPdf = (url: string) => {
        window.open(url, '_blank');
    };

    // 打开图片预览
    const openImagePreview = (index: number) => {
        setCurrentImageIndex(index);
        setOpenLightbox(true);
    };

    // 格式化日期
    const formatDate = (date: Date | undefined) => {
        if (!date) return '-';
        return format(date, 'PPP', { locale: currentLocale });
    };

    // 获取报销类型的本地化名称
    const getReimbursementTypeName = (type: ReimbursementType) => {
        return t(`reimbursements.types.${type}`);
    };

    // 获取报销状态的样式
    const getStatusStyle = (status: ReimbursementStatus) => {
        switch (status) {
            case ReimbursementStatus.PENDING:
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case ReimbursementStatus.APPROVED:
                return 'bg-green-100 text-green-800 border-green-200';
            case ReimbursementStatus.REJECTED:
                return 'bg-red-100 text-red-800 border-red-200';
            case ReimbursementStatus.PAID:
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // 处理删除报销记录
    const handleDelete = async () => {
        if (!id || !auth.currentUser) return;

        try {
            setDeleting(true);
            await deleteDoc(doc(db, 'reimbursements', id));
            navigate(-1);
        } catch (err) {
            console.error('删除报销记录失败:', err);
            setError(t('reimbursements.deleteError'));
            setDeleting(false);
        }
    };

    // 判断是否可以编辑或删除
    const canEditOrDelete = () => {
        if (!reimbursement) return false;
        // 只有待审核状态的报销记录可以编辑或删除
        return reimbursement.status === ReimbursementStatus.PENDING;
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8 mt-32">
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent"></div>
                </div>
            </div>
        );
    }

    if (error || !reimbursement) {
        return (
            <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8 mt-32">
                <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
                    <p>{error || t('reimbursements.notFound')}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                    >
                        {t('common.back')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8  mt-32">
            {/* 删除确认对话框 */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reimbursements.deleteConfirm')}</h3>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className={`px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white transition-colors ${deleting ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                {deleting ? t('common.deleting') : t('common.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">{reimbursement.title}</h2>
                        <span className={`mt-1 inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyle(reimbursement.status)}`}>
                            {t(`reimbursements.status.${reimbursement.status}`)}
                        </span>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {t('common.back')}
                        </button>
                        {canEditOrDelete() && (
                            <>
                                <button
                                    onClick={() => navigate(`/profile/reimbursements/edit/${reimbursement.id}`)}
                                    className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {t('common.edit')}
                                </button>
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    {t('common.delete')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">{t('reimbursements.amount')}</dt>
                            <dd className="mt-1 text-lg font-semibold text-gray-900">${reimbursement.amount.toFixed(2)}</dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">{t('reimbursements.type')}</dt>
                            <dd className="mt-1 text-sm text-gray-900">{getReimbursementTypeName(reimbursement.type)}</dd>
                        </div>
                        <div className="sm:col-span-2">
                            <dt className="text-sm font-medium text-gray-500">{t('reimbursements.description')}</dt>
                            <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{reimbursement.description}</dd>
                        </div>

                        {/* 时间信息 */}
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">{t('reimbursements.createdAt')}</dt>
                            <dd className="mt-1 text-sm text-gray-900">{formatDate(reimbursement.createdAt)}</dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">{t('reimbursements.updatedAt')}</dt>
                            <dd className="mt-1 text-sm text-gray-900">{formatDate(reimbursement.updatedAt)}</dd>
                        </div>

                        {/* 审核信息 */}
                        {reimbursement.status !== ReimbursementStatus.PENDING && (
                            <>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">{t('reimbursements.reviewedAt')}</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{formatDate(reimbursement.reviewedAt)}</dd>
                                </div>
                                {reimbursement.reviewComment && (
                                    <div className="sm:col-span-2">
                                        <dt className="text-sm font-medium text-gray-500">{t('reimbursements.reviewComment')}</dt>
                                        <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{reimbursement.reviewComment}</dd>
                                    </div>
                                )}
                            </>
                        )}

                        {/* 支付信息 */}
                        {reimbursement.status === ReimbursementStatus.PAID && (
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">{t('reimbursements.paymentDate')}</dt>
                                <dd className="mt-1 text-sm text-gray-900">{formatDate(reimbursement.paymentDate)}</dd>
                            </div>
                        )}
                    </dl>
                </div>

                {/* 收据和文档 */}
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reimbursements.receipts')}</h3>
                    {processedReceipts.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {processedReceipts.map((fileInfo, index) => (
                                <div key={index} className="relative group">
                                    <div className="h-40 border border-gray-300 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        {fileInfo.type === FileType.PDF ? (
                                            <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                                                <svg className="h-12 w-12 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                </svg>
                                                <span className="mt-2 text-sm text-gray-500">PDF</span>
                                                <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md"
                                                    onClick={() => showPdf(fileInfo.url)}>
                                                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1.5 bg-black bg-opacity-50 rounded-full">
                                                        {t('reimbursements.preview')}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <img
                                                    src={fileInfo.url}
                                                    alt={`收据 ${index + 1}`}
                                                    className="h-full w-full object-cover"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md"
                                                    onClick={() => openImagePreview(index)}>
                                                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1.5 bg-black bg-opacity-50 rounded-full">
                                                        {t('reimbursements.preview')}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="mt-2 text-sm text-gray-500">{t('reimbursements.noReceipts')}</p>
                        </div>
                    )}

                    {processedDocuments.length > 0 && (
                        <div className="mt-8">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reimbursements.additionalDocuments')}</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {processedDocuments.map((fileInfo, index) => (
                                    <div key={index} className="relative group">
                                        <div className="h-40 border border-gray-300 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                            {fileInfo.type === FileType.PDF ? (
                                                <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                                                    <svg className="h-12 w-12 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="mt-2 text-sm text-gray-500">PDF</span>
                                                    <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md"
                                                        onClick={() => showPdf(fileInfo.url)}>
                                                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1.5 bg-black bg-opacity-50 rounded-full">
                                                            {t('reimbursements.preview')}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : fileInfo.type === FileType.WORD ? (
                                                <div className="flex flex-col items-center justify-center h-full bg-blue-50">
                                                    <svg className="h-12 w-12 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="mt-2 text-sm text-gray-500">Word</span>
                                                    <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    <a
                                                        href={fileInfo.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md"
                                                    >
                                                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1.5 bg-black bg-opacity-50 rounded-full">
                                                            {t('reimbursements.preview')}
                                                        </span>
                                                    </a>
                                                </div>
                                            ) : fileInfo.type === FileType.EXCEL ? (
                                                <div className="flex flex-col items-center justify-center h-full bg-green-50">
                                                    <svg className="h-12 w-12 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="mt-2 text-sm text-gray-500">Excel</span>
                                                    <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    <a
                                                        href={fileInfo.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md"
                                                    >
                                                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1.5 bg-black bg-opacity-50 rounded-full">
                                                            {t('reimbursements.preview')}
                                                        </span>
                                                    </a>
                                                </div>
                                            ) : (
                                                <div>
                                                    <img
                                                        src={fileInfo.url}
                                                        alt={`附加文档 ${index + 1}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md"
                                                        onClick={() => openImagePreview(processedReceipts.length + index)}>
                                                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1.5 bg-black bg-opacity-50 rounded-full">
                                                            {t('reimbursements.preview')}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* 图片预览 */}
            <Lightbox
                open={openLightbox}
                close={() => setOpenLightbox(false)}
                slides={[
                    ...processedReceipts
                        .filter(file => file.type === FileType.IMAGE)
                        .map(file => ({ src: file.url, alt: file.name })),
                    ...processedDocuments
                        .filter(file => file.type === FileType.IMAGE)
                        .map(file => ({ src: file.url, alt: file.name }))
                ]}
                index={currentImageIndex}
                plugins={[Zoom]}
                zoom={{
                    maxZoomPixelRatio: 5, // 最大缩放比例
                    scrollToZoom: true,   // 允许滚轮缩放
                    wheelZoomDistanceFactor: 100,  // 滚轮缩放速度
                    doubleClickMaxStops: 2,  // 双击缩放级别
                    doubleClickDelay: 300,   // 双击延迟
                }}
                render={{
                    buttonPrev: () => null,
                    buttonNext: () => null,
                    iconZoomIn: () => null,  // 隐藏默认的缩放图标
                    iconZoomOut: () => null,  // 隐藏默认的缩放图标
                }}
            />
        </div>
    );
};

export default ReimbursementDetail; 